import { useFindOne, useGlobalAction } from "@gadgetinc/react";
import { CheckIcon } from "@heroicons/react/16/solid";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { api } from "../../../api";
import { OverflowFrame, PageHeading } from "../../../externalComponents";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Field,
  FieldGroup,
} from "../../../externalComponents/tailwind/fieldset";
import { Input } from "../../../externalComponents/tailwind/input";
import { Text } from "../../../externalComponents/tailwind/text";
import { WhatsappQualityScoreBanner } from "../../../externalComponents/WhatsappQualityScoreBanner";
import { isDateTimeAfterToday } from "../../../functions/date-utils";
import { CustomerContext } from "../../../providers";
import {
  AlertDialog,
  ContactsBreakdownDialog,
  Scheduler,
  SendCampaignOptions,
  SendCampaignPacing,
  SendCampaignRecipients,
  SendNowDialog,
  SendTestDialog,
  SentDialog,
  stepCampaignEnum,
  TemplatePreviewMarkup,
  whatsappMessagingLimits,
} from "./shared";

const SendCampaignPage = () => {
  const [form, setForm] = useState({
    type: null,
    sendOnlyToOptins: null,
    contactsBreakdown: null,
    isSendingNow: null,
    fetchingContacts: false,
    selectorId: null,
    contactListId: null,
    selectedContactsCount: 0,
    selectedOptions: [],
    inputValue: "",
    scheduleDateTime: new Date(),
    segmentsKlaviyo: null,
    segmentsBrevo: null,
    segmentsShopify: null,
    listsHubspot: null,
    segmentsSimioSegment: null,
    /**
     * The step to fill, highlighted by a yellow border.
     * Default is the first step.
     */
    stepToFill: null,
    /**
     * To be sure the user fills the steps in order, collapse them until they selected something in the previous step
     * @example
     * setForm(_form => ({..._form, expanded: new Set(_form.expanded).add(stepCampaignEnum.sendingMode)}))
     */
    expanded: new Set(),
    pacing: {
      paceOverDays: 1,
      planning: [],
      skippedDaysSet: new Set(),
      disclaimer: "",
      effectiveContactsCount: 0,
    },
  });

  const location = useLocation();
  const campaignId = location.pathname.split("/").slice(-1)[0];
  const { user, customer, integrations } = useContext(CustomerContext);

  const [{ data, fetching }] = useFindOne(api.campaign, campaignId, {
    select: {
      id: true,
      scenario: {
        id: true,
      },
      customerId: true,
      customer: {
        klaviyoToken: true,
        klaviyoAccessToken: true,
        apiKeyHubspot: true,
        apiKeyBrevo: true,
      },
      name: true,
      whatsappTemplate: {
        id: true,
        whatsappTemplateName: true,
        headerParameters: true,
        cards: true,
      },
      sendingsStatus: true,
    },
  });

  const [isImageOrVideoMissing, setIsImageOrVideoMissing] = useState(null);

  const [modalRef, setModalRef] = useState({
    alert: false,
    sendTest: false,
    sendNow: false,
    sent: false,
    /**
     * @example
     * 0: dialog not shown
     * 1: breakdown related to segment's contacts that are optin in Simio
     * 2: breakdown related to segment's contacts unless they are opted out
     */
    contactsBreakdownMode: 0,
  });

  const updateSegmentsKlaviyo = useCallback(
    (segmentsKlaviyo) => {
      setForm((_form) => ({
        ..._form,
        segmentsKlaviyo,
      }));
    },
    [data],
  );

  const updateSegmentsBrevo = useCallback(
    (segmentsBrevo) => {
      setForm((_form) => ({
        ..._form,
        segmentsBrevo,
      }));
    },
    [data],
  );

  const updateSegmentsShopify = useCallback(
    (segmentsShopify) => {
      setForm((_form) => ({
        ..._form,
        segmentsShopify,
      }));
    },
    [data],
  );

  const updateListsHubspot = useCallback(
    (listsHubspot) => {
      setForm((_form) => ({
        ..._form,
        listsHubspot,
      }));
    },
    [data],
  );

  const updateSegmentsSimioSegment = useCallback(
    (segmentsSimioSegment) => {
      setForm((_form) => ({
        ..._form,
        segmentsSimioSegment,
      }));
    },
    [data],
  );

  useEffect(() => {
    const headerParams = data?.whatsappTemplate?.headerParameters ?? {};
    if (!headerParams || Object.keys(headerParams).length === 0) {
      setIsImageOrVideoMissing(false);
      return;
    }
    if (
      (headerParams.image && headerParams.image?.link !== "") ||
      (headerParams.video && headerParams.video?.link !== "")
    ) {
      setIsImageOrVideoMissing(false);
    } else {
      console.log("Header missing image or video link");
      setIsImageOrVideoMissing(true);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      // define an async function to make the request
      const klaviyoSegmentsHttpRouteRequest = async () => {
        const result = await api.klaviyo.getSegments({
          customerId: data.customerId,
        });
        // use state hooks to handle response data in your component
        updateSegmentsKlaviyo(result.segments);
      };

      const shopifySegmentsHttpRouteRequest = async () => {
        // make sure to use your Gadget app domain and route!
        const result = await api.getShopifySegments();
        // use state hooks to handle response data in your component
        updateSegmentsShopify(result);
      };

      const brevoSegmentsHttpRouteRequest = async () => {
        const result = await api.brevo.getSegments({
          customerId: data.customerId,
        });
        updateSegmentsBrevo(result.segments);
      };

      const hubspotListsHttpRouteRequest = async () => {
        const hubspotLists = await api.getHubspotLists({
          apiKeyHubspot: data.customer?.apiKeyHubspot,
        });
        updateListsHubspot(hubspotLists.lists);
      };

      const simioSegmentSegmentsHttpRouteRequest = async () => {
        const segments = await api.segment.findMany({
          first: 250,
          filter: {
            customerId: { equals: data.customerId },
            type: { equals: "materialized" },
            isArchived: { notEquals: true },
          },
          select: {
            id: true,
            name: true,
            lastContactsCount: true,
            filters: true,
          },
          sort: {
            name: "Ascending",
          },
        });
        updateSegmentsSimioSegment(segments);
      };

      if (integrations.klaviyo) {
        // call async function and handle errors
        klaviyoSegmentsHttpRouteRequest().catch(console.error);
      }
      if (integrations.brevo && data.customer?.apiKeyBrevo) {
        // call async function and handle errors
        brevoSegmentsHttpRouteRequest().catch(console.error);
      }
      if (integrations.shopify) {
        // call async function and handle errors
        shopifySegmentsHttpRouteRequest().catch(console.error);
      }
      if (integrations.hubspot) {
        // call async function and handle errors
        hubspotListsHttpRouteRequest().catch(console.error);
      }

      // Always fetch Simio Segment segments (no integration required)
      simioSegmentSegmentsHttpRouteRequest().catch(console.error);
    }
  }, [data]);

  const [tags, setTags] = useState(null);

  useEffect(() => {
    const temp = async () => {
      const tags = await api.tag.findMany({
        first: 250,
        filter: {
          customerId: { equals: user.customerId },
        },
        sort: {
          id: "Descending",
        },
        select: {
          id: true,
          name: true,
        },
      });
      setTags(
        tags?.map((t) => ({
          id: t.id,
          name: t.name,
        })),
      );
    };
    temp().catch(console.error);
  }, []);

  const [result, getWhatsAppTemplates] = useGlobalAction(
    api.getWhatsAppTemplates,
    {
      customerId: user.customerId,
      limit: "5000",
      templateName: data?.whatsappTemplate?.whatsappTemplateName,
    },
  );
  const {
    data: templateData,
    error: templateError,
    fetching: templateFetching,
  } = result;

  useEffect(
    () => {
      if (data?.whatsappTemplate?.whatsappTemplateName) {
        getWhatsAppTemplates({
          templateName: data?.whatsappTemplate?.whatsappTemplateName,
          limit: "5000",
          customerId: user.customerId,
        });
      }
    },
    // C'est pas clean, mais quand il y a une erreur ça refait la requete. au cas où la requete renvoyait rien
    [templateError, data?.whatsappTemplate?.whatsappTemplateName],
  );

  const template = useMemo(() => {
    return ((templateData?.waba_templates ?? templateData) || []).find(
      ({ name }) => name === data?.whatsappTemplate?.whatsappTemplateName,
    );
  }, [templateData, data]);

  const templateStatus = template?.status?.toLowerCase();

  const templateErrorMessage = useMemo(() => {
    if (!template || templateStatus === "approved") {
      return null;
    }

    switch (templateStatus) {
      case "pending":
        return "The selected template is waiting for approval by Meta. You can't send this campaign.";
      case "rejected":
        return "The selected template has been rejected by Meta. You can't send this campaign.";
      default:
        return `The selected template status is ${template?.status || "invalid"}. You can't send this campaign.`;
    }
  }, [template, templateStatus]);

  return fetching || templateFetching ? (
    <LoadingSpinner />
  ) : tags && data ? (
    <>
      <PageHeadingMarkup
        data={data}
        form={form}
        user={user}
        isImageOrVideoMissing={isImageOrVideoMissing}
        headerType={data?.whatsappTemplate?.headerParameters?.type}
        modalRef={modalRef}
        setModalRef={setModalRef}
        template={template}
        templateStatus={templateStatus}
      />
      <OverflowFrame className="flex-1">
        <div className="max-w-[1024px] m-auto mt-5 flex ">
          <PageMarkupNewProduct
            customer={customer}
            data={data}
            integrations={integrations}
            form={form}
            setForm={setForm}
            tags={tags}
            modalRef={modalRef}
            setModalRef={setModalRef}
          />
          <FieldGroup className="w-[390px] pl-[40px] sticky top-5 self-start">
            {!template && (
              <Text className="text-red-500 font-bold border rounded-customBorderRadius p-3 mt-5">
                No template selected. You can't send this campaign.
              </Text>
            )}
            {template && templateStatus === "approved" && (
              <TemplatePreviewMarkup data={data} template={template} />
            )}
            {templateErrorMessage && (
              <Text className="text-red-500 font-bold border rounded-customBorderRadius p-3 mt-5">
                {templateErrorMessage}
              </Text>
            )}
          </FieldGroup>
        </div>
      </OverflowFrame>
    </>
  ) : (
    <LoadingSpinner />
  );
};

function PageMarkupNewProduct({
  customer,
  data,
  integrations,
  form,
  setForm,
  tags,
  setModalRef,
}) {
  const deselectedOptionsTags = useMemo(() => {
    return tags == null
      ? []
      : tags?.map((t) => {
          return {
            value: t.id,
            label: t.name,
          };
        });
  }, []);

  let isScheduled = data.sendingsStatus?.some((s) => {
    if (!s.scheduled) return false;

    // Only show banner if the sending was created more than 30 seconds ago
    if (s.createdAt) {
      // Parse the createdAt string as UTC to avoid timezone issues
      const createdAt = new Date(s.createdAt + "Z"); // Ensure UTC parsing
      const now = new Date();
      const timeDiffInSeconds = (now - createdAt) / 1000;
      return timeDiffInSeconds > 30;
    }

    // If no createdAt, don't show the banner for new sendings
    return false;
  });
  const [isCancelingFutureSendings, setIsCancelingFutureSendings] =
    useState(false);

  const stepToFill = "border-[3px] border-[#ffeb3b]";

  return (
    <>
      <FieldGroup className="grow w-[70%] pb-5">
        <div>
          {isScheduled && (
            <Field className="border border-pink rounded-customBorderRadius p-3 mb-5">
              <Text className="font-bold">
                ⚠️ This campaign is already scheduled
              </Text>
              <Text className="font-medium my-2">
                {data?.name} is scheduled. If you reschedule, both batches will
                be sent. You might want to cancel scheduled messages first.
              </Text>
              <Button
                color="primaryButton"
                loading={isCancelingFutureSendings}
                onClick={async () => {
                  setIsCancelingFutureSendings(true);
                  await api.campaign.cancelFutureSendings(data.id);
                  setIsCancelingFutureSendings(false);
                }}
              >
                Cancel future messages
              </Button>
            </Field>
          )}

          <WhatsappQualityScoreBanner
            className="mt-5 h-[12rem]"
            customer={customer}
          />

          {deselectedOptionsTags != [] && (
            <>
              <SendCampaignRecipients
                className={!form.stepToFill && stepToFill}
                api={api}
                customerId={data?.customerId}
                integrations={integrations}
                form={form}
                setForm={setForm}
              />

              <SendCampaignOptions
                className={
                  form.stepToFill === stepCampaignEnum.sendingMode && stepToFill
                }
                expanded={form.expanded.has(stepCampaignEnum.sendingMode)}
                form={form}
                setForm={setForm}
                setModalRef={setModalRef}
              />
            </>
          )}
        </div>
        <Scheduler
          className={
            form.stepToFill === stepCampaignEnum.scheduleSending && stepToFill
          }
          expanded={form.expanded.has(stepCampaignEnum.scheduleSending)}
          form={form}
          setForm={setForm}
        />

        <SendCampaignPacing
          className={form.stepToFill === stepCampaignEnum.pacing && stepToFill}
          expanded={form.expanded.has(stepCampaignEnum.pacing)}
          form={form}
          setForm={setForm}
          messagingLimit={whatsappMessagingLimits[customer.messagingLimit]}
        />
      </FieldGroup>
    </>
  );
}

function PageHeadingMarkup({
  user,
  data,
  form,
  isImageOrVideoMissing,
  modalRef,
  setModalRef,
  headerType,
  template,
  templateStatus,
}) {
  const { id, name, sendingsStatus, scenario } = data;

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingInput, setIsEditingInput] = useState(false);
  const [campaignName, setCampaignName] = useState(name);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const inputRef = useRef(null);
  const canRenameCampaign =
    !sendingsStatus.length ||
    sendingsStatus.every((sending) => !sending.sent && !sending.ongoing);

  useEffect(() => {
    if (isEditingInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingInput]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setCampaignName(e.target.value);
      handleRename(campaignName);
    }
  };

  const handleRename = async () => {
    setIsRenameLoading(true);
    await api.campaign.update(id, {
      name: campaignName,
    });
    if (scenario?.id) {
      await api.scenario.update(scenario.id, {
        name: campaignName,
      });
    }
    setIsEditingInput(false);
    setIsRenameLoading(false);
  };

  const { isSendingNow, scheduleDateTime } = form;

  const sendCampaign = useCallback(async () => {
    setIsLoading(true);

    // At this point, we know that the user has selected the send date. Then isSendingNow is either true or false.
    if (isSendingNow || isDateTimeAfterToday(scheduleDateTime)) {
      // Enqueue the actual sending creation server-side: it keeps running even
      // if this tab closes or the network drops right after this call returns.
      await api.enqueue(
        api.createCampaignSendings,
        {
          campaignId: id,
          customerId: user.customerId,
          type: form.type,
          selector: form.selectorId,
          contactListId: form.contactListId,
          sendOnlyToOptins: form.sendOnlyToOptins,
          isSendingNow: form.isSendingNow,
          scheduleDateTime: (
            form.isSendingNow ? new Date() : form.scheduleDateTime
          ).toISOString(),
          pacing: {
            paceOverDays: form.pacing.paceOverDays,
            planning: form.pacing.planning.map(
              ({ dayIndex, contacts, skipped }) => ({
                dayIndex,
                contacts,
                skipped,
              }),
            ),
          },
        },
        {
          queue: { name: `campaign-sending-${id}`, maxConcurrency: 1 },
        },
      );

      setModalRef((_modalRef) => ({
        ..._modalRef,
        sent: true,
      }));
    }
    setIsLoading(false);
  });

  return (
    <>
      <PageHeading
        title={name}
        titleAction={canRenameCampaign}
        handleTitleAction={() => {
          setIsEditingInput(true);
        }}
        primaryAction={isSendingNow === false ? "Schedule" : "Send"}
        isPrimaryLoading={isLoading}
        isPrimaryDisabled={
          form.fetchingContacts ||
          (!isSendingNow && !isDateTimeAfterToday(scheduleDateTime)) ||
          form.sendOnlyToOptins === null ||
          form.isSendingNow === null ||
          !form.pacing?.effectiveContactsCount ||
          !template ||
          templateStatus !== "approved"
        }
        handlePrimaryAction={async () => {
          setIsEditingInput(false);
          if (isImageOrVideoMissing) {
            setModalRef((_modalRef) => ({
              ..._modalRef,
              alert: true,
            }));
            return;
          }
          if (isSendingNow) {
            // Ask for confirmation before to send the campaign.
            setModalRef((_modalRef) => ({
              ..._modalRef,
              sendNow: true,
            }));
          } else {
            // Schedule the campaign
            await sendCampaign();
          }
        }}
        secondaryAction="Send a test"
        handleSecondaryAction={async () => {
          if (isImageOrVideoMissing) {
            setModalRef((_modalRef) => ({
              ..._modalRef,
              alert: true,
            }));
            return;
          }
          setModalRef((_modalRef) => ({
            ..._modalRef,
            sendTest: true,
          }));
        }}
      />

      {isEditingInput && (
        <>
          <div className="flex items-center" style={{ maxWidth: "75%" }}>
            <Input
              ref={inputRef}
              className="absolute top-[-60px] z-10 left-[9px] font-title font-bold leading-7 text-gray-900 dark:text-white"
              style={{ maxWidth: "100%", margin: "0", fontSize: "30px" }}
              value={campaignName}
              onChange={(e) => {
                setCampaignName(e.target.value);
              }}
              autoComplete="off"
              onKeyUp={(e) => {
                handleKeyPress(e);
              }}
            />
            <Button
              outline
              className="absolute top-[-60px] left-[9px] bg-white h-12 w-12"
              onClick={() => {
                handleRename();
              }}
              loading={isRenameLoading}
            >
              <CheckIcon className="w-20 h-20" />
            </Button>
          </div>
        </>
      )}

      <SentDialog
        modalRef={modalRef}
        name={name}
        form={form}
        navigate={navigate}
      />
      <AlertDialog
        modalRef={modalRef}
        setModalRef={setModalRef}
        data={data}
        headerType={headerType}
        navigate={navigate}
      />
      <SendNowDialog
        modalRef={modalRef}
        setModalRef={setModalRef}
        sendCampaign={sendCampaign}
      />
      <SendTestDialog
        modalRef={modalRef}
        setModalRef={setModalRef}
        user={user}
      />
      <ContactsBreakdownDialog
        modalRef={modalRef}
        setModalRef={setModalRef}
        contactsBreakdown={form.contactsBreakdown}
      />
    </>
  );
}

export default SendCampaignPage;
